// Respaldo bilingue de la verificacion de identidad y la carga de documentos:
// INE, pasaporte, FM, constancia fiscal, prueba de vida y el procesamiento de la
// imagen que suben.
//
// POR QUE EXISTE: estos avisos estaban escritos solo en INGLES —el reverso del
// defecto de los que estaban solo en español—, asi que quien tenia la interfaz en
// español leia "You must upload both INE images" en medio de una pantalla en
// español. Ademas la misma idea estaba redactada de tres formas distintas bajo la
// misma clave del esquema (AL0049 decia "Upload the passport image.", "You must
// upload a passport image" y "Debes subir una imagen del pasaporte" segun la
// pantalla); aqui queda una sola redaccion por concepto, que es lo que el esquema
// remoto entrega de todos modos cuando publica la clave.
//
// Vive aparte de `propertyMessages` porque la identidad no es del dominio de los
// inmuebles: la usan por igual el alta de socio de negocio, el broker, la
// verificacion del perfil y los acompanantes de una reserva.

import { normalizeInterfaceLanguage } from "../interfaceLanguage";

const identityMessages = {
  es: {
    uploadBothIneImages: "Debes subir ambas imágenes de la INE",
    ineUploadFailed: "Error al enviar la INE, intenta nuevamente con otra imagen",
    ineFormatInvalid: "El formato de la INE no es válido (frente y reverso).",
    uploadPassportImage: "Debes subir una imagen del pasaporte",
    passportUploadFailed: "Error al enviar Pasaporte",
    uploadFmImage: "Debes subir una imagen del documento FM",
    uploadYourDocuments: "Debes subir tus documentos",
    selectDocumentTypeFirst: "Primero elige Pasaporte o INE.",
    selectIdType: "Selecciona un tipo de identificación.",
    unsupportedDocumentType: "Tipo de documento no admitido.",
    unsupportedDocFormat: "Formato no soportado. Sube una imagen o PDF.",
    imageRequired: "Se debe agregar imagen",
    missingUserId: "Falta el identificador del usuario.",
    identificationValidated: "¡Identificación validada correctamente!",
    sendCodeFailed: "Error al enviar el código, inténtalo de nuevo más tarde.",
    cameraAccessFailed: "No se pudo acceder a la cámara o al micrófono.",
  },
  en: {
    uploadBothIneImages: "You must upload both images of your INE",
    ineUploadFailed: "Could not send the INE, try again with another image",
    ineFormatInvalid: "INE format is not valid (front and back).",
    uploadPassportImage: "You must upload an image of your passport",
    passportUploadFailed: "Could not send the passport",
    uploadFmImage: "You must upload an image of your FM document",
    uploadYourDocuments: "You must upload your documents",
    selectDocumentTypeFirst: "Select Passport or INE first.",
    selectIdType: "Select an ID type.",
    unsupportedDocumentType: "Unsupported document type.",
    unsupportedDocFormat: "Unsupported format. Upload an image or a PDF.",
    imageRequired: "An image must be added",
    missingUserId: "The user id is missing.",
    identificationValidated: "Identification validated successfully!",
    sendCodeFailed: "Error sending the code, please try again later.",
    cameraAccessFailed: "Could not access the camera or the microphone.",
  },
};

// Tabla aparte porque sus consumidores —UploadCard e IdentityUploadView— la leen
// con `t(clave)` usando los nombres de clave del esquema remoto, no camelCase.
// Estaba escrita en un objeto `enUploadMessages` solo en ingles dentro de
// UploadCard.jsx. IdentityUploadView, ademas, tenia `t = (k) => texts?.[k] || k`:
// sin clave en el esquema pintaba la clave cruda ("AL_NO_HEIC") en pantalla.
const uploadCardMessages = {
  es: {
    AL_NO_HEIC:
      "El formato HEIC/HEIF no está soportado. En iPhone ve a Ajustes → Cámara → Formatos → Más compatible.",
    AL_FMT_INV: "Formato de archivo no soportado. Solo se permiten JPG, PNG o WebP.",
    AL_PESO_MAX: "El archivo es demasiado grande. El tamaño máximo es 5 MB.",
    AL_ERR_IMG: "No pudimos procesar la imagen. Intenta con otra foto.",
    LB_PROC_IMG: "Procesando imagen…",
    UI_REPLACE: "Reemplazar",
    UI_UPLOAD: "Subir imagen",
    UI_TIPS_TITLE: "Recomendaciones",
    UI_AVOID_TITLE: "Evita esto",
    UI_TIP_BLUR: "Borrosa o desenfocada",
    UI_TIP_CROPPED: "Cortada o con reflejos",
    UI_TIP_DARK: "Muy oscura o ilegible",
    UI_FORMATS_NOTE:
      "Formatos permitidos: JPG, PNG, WebP. En iPhone: Ajustes → Cámara → Formatos → Más compatible para capturar en JPG.",
  },
  en: {
    AL_NO_HEIC:
      "HEIC/HEIF format is not supported. On iPhone, go to Settings → Camera → Formats → Most Compatible.",
    AL_FMT_INV: "Unsupported file format. Only JPG, PNG or WebP are allowed.",
    AL_PESO_MAX: "The file is too large. Maximum size is 5 MB.",
    AL_ERR_IMG: "We couldn't process the image. Please try with a different photo.",
    LB_PROC_IMG: "Processing image…",
    UI_REPLACE: "Replace",
    UI_UPLOAD: "Upload image",
    UI_TIPS_TITLE: "Recommendations",
    UI_AVOID_TITLE: "Avoid this",
    UI_TIP_BLUR: "Blurry / out of focus",
    UI_TIP_CROPPED: "Cropped / with glare",
    UI_TIP_DARK: "Too dark / unreadable",
    UI_FORMATS_NOTE:
      "Allowed formats: JPG, PNG, WebP. On iPhone: Settings → Camera → Formats → Most Compatible to capture JPG.",
  },
};

export const getIdentityMessages = (language) =>
  identityMessages[normalizeInterfaceLanguage(language)];

export const getUploadCardMessages = (language) =>
  uploadCardMessages[normalizeInterfaceLanguage(language)];
